import { useRef, useState } from "react"
import { Undo2, Redo2 } from "lucide-react"
import { mcpBus } from "@/mcp/studio-mcp-bus"
import { useUndoStateStore } from "@/mcp/undo-state-store"
import { useStudioLocale } from "@/i18n"

/**
 * The machine name of the history command, NOT its label. It routes the MCP
 * tool and prefixes the failure line; the words a reader sees come from
 * `strings.ide.undo` / `.redo` below. Kept English on purpose — the failure
 * line is unlocalized on every locale today and translating only the button
 * would have split one sentence across two languages.
 */
type HistoryAction = "Undo" | "Redo"

export function UndoRedoCluster() {
  const { strings } = useStudioLocale()
  const canUndo = useUndoStateStore((s) => s.canUndo)
  const canRedo = useUndoStateStore((s) => s.canRedo)
  const [pendingAction, setPendingAction] = useState<HistoryAction | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pendingRef = useRef(false)

  const runHistoryAction = async (
    action: HistoryAction,
    tool: "cogo.studio.undo" | "cogo.studio.redo",
  ) => {
    if (pendingRef.current) return
    pendingRef.current = true
    setPendingAction(action)
    setError(null)
    try {
      const result = await mcpBus.executeTrustedUi({ tool, params: {} })
      if (!result.success) {
        setError(`${action} failed: ${result.error ?? "Please try again."}`)
      }
    } catch (cause) {
      setError(
        `${action} failed: ${cause instanceof Error ? cause.message : "Please try again."}`,
      )
    } finally {
      pendingRef.current = false
      setPendingAction(null)
    }
  }

  const btnClass =
    "w-7 h-7 rounded-md flex items-center justify-center border border-border bg-card text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card transition-colors"

  return (
    <div
      className="flex min-w-0 items-center gap-1"
      aria-busy={Boolean(pendingAction)}
    >
      <button
        type="button"
        aria-label={strings.ide.undo}
        disabled={!canUndo || Boolean(pendingAction)}
        onClick={() => {
          void runHistoryAction("Undo", "cogo.studio.undo")
        }}
        className={btnClass}
      >
        <Undo2 size={14} strokeWidth={1.7} />
      </button>
      <button
        type="button"
        aria-label={strings.ide.redo}
        disabled={!canRedo || Boolean(pendingAction)}
        onClick={() => {
          void runHistoryAction("Redo", "cogo.studio.redo")
        }}
        className={btnClass}
      >
        <Redo2 size={14} strokeWidth={1.7} />
      </button>
      {pendingAction || error ? (
        <span
          className={`max-w-40 truncate text-[10px] ${
            error ? "text-destructive" : "text-muted-foreground"
          }`}
          role={error ? "alert" : "status"}
          title={error ?? `${pendingAction} in progress…`}
        >
          {error ?? `${pendingAction} in progress…`}
        </span>
      ) : null}
    </div>
  )
}
